import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faCopy, faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism"; // Using the "Tomorrow" theme

function DownloadDialog({ showDownloadButton }) {
  const [open, setOpen] = useState(false);
  const [msiexecCopied, setMsiexecCopied] = useState(null);
  const [powershellCopied, setPowershellCopied] = useState(null);
  const theme = useTheme();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setMsiexecCopied(null);
    setPowershellCopied(null);
  };

  const msiexecCommand = `msiexec --% /i "c:\\path\\to\\ZenzeroDeployment.Installer.msi" /l*v "c:\\logs\\params_install.log" CompanyName="Test Company" ClientCode=TST SiteName="TST01 - Test"`;

  const powershellCommand = `Start-Process msiexec -ArgumentList '/i "c:\\path\\to\\ZenzeroDeployment.Installer.msi" /qn CompanyName="Test Company" ClientCode=TST SiteName="TST01 - Test"' -Wait`;

  const copyToClipboard = async (text, setCopied) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied("success");
    } catch (error) {
      setCopied("error");
    }
    setTimeout(() => setCopied(null), 2000); // Reset the clipboard state after 2 seconds
  };

  return (
    <>
      {showDownloadButton && (
        <Tooltip arrow title="Download Installer">
          <IconButton
            onClick={handleOpen}
            sx={{
              color: theme.palette.info.main,
              marginRight: "16px",
              fontSize: "1.5rem", // Adjust for consistent sizing
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
          </IconButton>
        </Tooltip>
      )}

      {/* Dialog to show more information */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Download Installer</DialogTitle>
        <DialogContent dividers>
        <Typography variant="body1" sx={{mb:2}}>
            During installation, provide the clients code, name and site name when prompted, these should match RMM exactly.
          </Typography>
          <Typography variant="body1" fontWeight="bolder" gutterBottom>
            In order to run JimmyBot silently, specify these values as parameters to the installer:
          </Typography>
          <Box>

            {/* Code blocks */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Example 1 - Install JimmyBot from Cmd:
              </Typography>
              <IconButton onClick={() => copyToClipboard(msiexecCommand, setMsiexecCopied)}>
                <FontAwesomeIcon
                  icon={
                    msiexecCopied === "success"
                      ? faCheckCircle
                      : msiexecCopied === "error"
                      ? faTimesCircle
                      : faCopy
                  }
                  style={{
                    color:
                      msiexecCopied === "success"
                        ? theme.palette.success.main
                        : msiexecCopied === "error"
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                  }}
                />
              </IconButton>
            </Box>
            <SyntaxHighlighter
              language="bash"
              style={tomorrow}
              wrapLines={true}
              wrapLongLines={true}
              showLineNumbers={false}
            >
              {msiexecCommand}
            </SyntaxHighlighter>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Example 2 - Install JimmyBot silently using Powershell and wait for completion:
              </Typography>
              <IconButton onClick={() => copyToClipboard(powershellCommand, setPowershellCopied)}>
                <FontAwesomeIcon
                  icon={
                    powershellCopied === "success"
                      ? faCheckCircle
                      : powershellCopied === "error"
                      ? faTimesCircle
                      : faCopy
                  }
                  style={{
                    color:
                      powershellCopied === "success"
                        ? theme.palette.success.main
                        : msiexecCopied === "error"
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                  }}
                />
              </IconButton>
            </Box>
            <SyntaxHighlighter
              language="powershell"
              style={tomorrow}
              wrapLines={true}
              wrapLongLines={true}
              showLineNumbers={false}
            >
              {powershellCommand}
            </SyntaxHighlighter>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button href="https://files.zenzero.co.uk/JimmyBot/ZenzeroDeployment.Installer.msi" color="primary">
            Download Installer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DownloadDialog;
